import get from 'lodash/get';
import { createElement, useMemo, useState, useCallback } from 'react';
import { useAppState } from 'src/context';
import type { TPostBookingPayload } from 'src/types';
import type { TCreateEditBookingModalProps } from '../types';
import { OnCreateConfirmationModal } from '../components/OnCreateConfirmationModal';
import { DeleteModal } from '../components/DeleteModal';

export const useBookingModalState = () => {
    const {
        values: { modalData },
    } = useAppState();

    const bookingId = get(modalData?.options as TCreateEditBookingModalProps, 'booking.id', '');

    const [activeModal, setActiveModal] = useState<
        '' | 'deleteModal' | 'onCreateConfirmationModal'
    >('');

    const [confirmationData, setConfirmationData] = useState<
        (TPostBookingPayload & { message: string }) | undefined
    >();

    const handleCallDeleteModal = () => setActiveModal('deleteModal');

    const handleCloseActiveModal = useCallback(() => setActiveModal(''), []);

    const handleCallOnCreateConfirmationModal = (
        newConfirmationData: TPostBookingPayload & { message: string }
    ) => {
        setActiveModal('onCreateConfirmationModal');
        setConfirmationData(newConfirmationData);
    };

    const handleCloseCreateConfirmationModal = useCallback(() => {
        handleCloseActiveModal();
        setConfirmationData(undefined);
    }, [handleCloseActiveModal]);

    const subModals = useMemo(() => {
        return {
            onCreateConfirmationModal: confirmationData
                ? createElement(OnCreateConfirmationModal, {
                      ...confirmationData,
                      onClose: handleCloseCreateConfirmationModal,
                  })
                : null,
            deleteModal: bookingId
                ? createElement(DeleteModal, { id: bookingId, onClose: handleCloseActiveModal })
                : null,
        };
    }, [bookingId, confirmationData, handleCloseActiveModal, handleCloseCreateConfirmationModal]);

    return {
        activeModal,
        handleCallDeleteModal,
        handleCallOnCreateConfirmationModal,
        subModals,
    };
};
